import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Alert,
  Button,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  MobileStepper,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import type { SystemStyleObject } from '@mui/system';
import type { Theme } from '@mui/material/styles';
import { Controller } from 'react-hook-form';
import { NavigateFunction } from 'react-router';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import FormSection from 'src/components/form-section/form-section';
import { paths } from 'src/routes/paths';
import { ReactNode, useState } from 'react';
import { CLONE_FIELD_LABELS, CloneRequiredField } from '../lp-event-clone.utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Iconify from 'src/components/iconify';
import { RHFUploadSingleFileAccelerator } from 'src/components/hook-form/rhf-upload';
import RHFUrlMaskField from 'src/components/hook-form/rhf-url-mask-field';
import RHFGaMaskField from 'src/components/hook-form/rhf-ga-mask-field';
import RHFTextArea from 'src/components/hook-form/rhf-text-area';
import { BASE_URL } from 'src/config-global';
import { useMyDataStore } from 'src/store/me/me.store';

type Props = {
  formValues: any;
  methods: any;
  control: any;
  businessUnits?: any[];
  isEdit: boolean;
  isClone?: boolean;
  pendingCloneFields?: CloneRequiredField[];
  cloneBenefits?: ReactNode;
  isSubmitting: boolean;
  onSubmit: VoidFunction;
  onBack?: VoidFunction;
  navigate: NavigateFunction;
  handleDropBannerImage: (acceptedFiles: File[]) => void;
  handleDropContentImage: (acceptedFiles: File[]) => void;
  handleDropRegisterPageBannerImage: (acceptedFiles: File[]) => void;
  handleDropRegisterPageContentImage: (acceptedFiles: File[]) => void;
  handleDropHomePageBannerImage: (acceptedFiles: File[]) => void;
};

export default function LpEventsForm({
  formValues,
  methods,
  control,
  businessUnits,
  isEdit,
  isClone = false,
  pendingCloneFields = [],
  cloneBenefits,
  isSubmitting,
  handleDropBannerImage,
  handleDropContentImage,
  handleDropRegisterPageBannerImage,
  handleDropRegisterPageContentImage,
  handleDropHomePageBannerImage,
  onSubmit,
  onBack,
  navigate,
}: Props) {
  const theme = useTheme();

  const permissions = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.benefits);
  const cloneFieldSx = (field: CloneRequiredField): SystemStyleObject<Theme> =>
    isClone
      ? {
          '& .MuiOutlinedInput-root': {
            bgcolor: '#F2F5FC',
            '& fieldset': {
              borderColor: methods.formState.errors[field] ? 'error.main' : '#0033A0',
              borderWidth: 2,
            },
          },
        }
      : {};
  const cloneBadge = isClone
    ? 'Campo obrigatório para duplicação — Edite antes de salvar'
    : undefined;
  const defaultSaveLabel = isEdit ? 'Editar Evento do Viva Visitante' : 'Cadastrar Evento do Viva Visitante';

  const exampleImages: any = {
    'login-page': ['page'],
    'register-page': ['page', 'mobile'],
    'home-page': ['page'],
    'benefits-page': ['title'],
  };
  const [openExample, setOpenExample] = useState<string[]>([]);
  const [image, setImage] = useState<any>({
    'login-page': 'page',
    'register-page': 'page',
    'home-page': 'page',
    'benefits-page': 'title',
  });
  const [step, setStep] = useState<any>({
    'register-page': 0,
  });

  const handleChangeExample = (section: string) => {
    if (openExample.includes(section)) {
      setOpenExample([...openExample.filter((f) => f !== section)]);
    } else {
      setOpenExample([...openExample, section]);
    }
  };

  const handleNext = (section: string) => {
    const sectionImages = exampleImages[section];
    setImage({ ...image, [section]: sectionImages[step[section] + 1] });
    setStep({ ...step, [section]: step[section] + 1 });
  };

  const handleBack = (section: string) => {
    const sectionImages = exampleImages[section];
    setImage({ ...image, [section]: sectionImages[step[section] - 1] });
    setStep({ ...step, [section]: step[section] - 1 });
  };

  return (
    <FormProvider methods={methods} onSubmit={onSubmit} noValidate>
      <Stack spacing={3}>
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={1}
              sx={{ p: 1 }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                {field.value === 'active' ? 'Ativo' : 'Inativo'}
              </Typography>
              <Switch
                checked={field.value === 'active'}
                onChange={(_, checked) => field.onChange(checked ? 'active' : 'inactive')}
                disabled={isEdit ? !permissions?.update : !permissions?.create}
                inputProps={{ 'aria-label': 'Status' }}
              />
            </Stack>
          )}
        />
        {isClone && (
          <Alert severity="info">
            Rascunho da cópia: edite o Google Analytics e as duas datas. O evento e seus itens serão
            criados somente ao salvar.
          </Alert>
        )}
        <FormSection index={1} title="Informações">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <RHFTextField name="name" label="Nome" />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField
                name="detail"
                label="Detalhe"
                required
                helperText="Exibido junto às datas e à unidade de negócio no Viva Visitante."
              />
            </Grid>
          </Grid>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="startDate"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  sx={[{ flex: 1 }, cloneFieldSx('startDate')]}
                  closeOnSelect
                  label="Data de Início"
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message || cloneBadge,
                      inputProps: {
                        readOnly: true,
                      },
                    },
                    day: {
                      sx: {
                        '&.MuiPickersDay-root.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: theme.palette.common.white,
                        },
                      },
                    },
                  }}
                  value={field.value}
                  onChange={(date) => {
                    if (date) {
                      const normalizedDate = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate()
                      );
                      field.onChange(normalizedDate);
                    } else {
                      field.onChange(null);
                    }
                  }}
                />
              )}
            />
            <Controller
              name="endDate"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  sx={[{ flex: 1 }, cloneFieldSx('endDate')]}
                  closeOnSelect
                  label="Data de Fim"
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message || cloneBadge,
                      inputProps: {
                        readOnly: true,
                      },
                    },
                    day: {
                      sx: {
                        '&.MuiPickersDay-root.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: theme.palette.common.white,
                        },
                      },
                    },
                  }}
                  value={field.value}
                  onChange={(date) => {
                    if (date) {
                      const normalizedDate = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate()
                      );
                      field.onChange(normalizedDate);
                    } else {
                      field.onChange(null);
                    }
                  }}
                />
              )}
            />
          </Stack>
          <RHFSelect
            disabled={isEdit}
            name="businessUnitId"
            label="Unidade de negócio"
            sx={{
              width: '100%',
              borderRadius: '3px',
            }}
          >
            {businessUnits?.map((option: any, index: number) => (
              <MenuItem key={index} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </RHFSelect>
        </FormSection>
        <FormSection index={2} title="URL e Google Analytics">
          <RHFUrlMaskField name="URLName" label="Nome da URL" />
          <Typography variant="caption" ml={2}>
            <Typography fontWeight="bold" variant="caption">
              URL Gerada:
            </Typography>{' '}
            {`${BASE_URL}${businessUnits?.find((b) => b.value === formValues.businessUnitId)?.URLName ?? ''}${formValues.URLName}`}
          </Typography>
          <Box sx={cloneFieldSx('GAPropertyId')}>
            <RHFGaMaskField
              name="GAPropertyId"
              label="Chave do Google Analytics"
              disabled={isEdit}
            />
            {isClone && (
              <Typography variant="caption" color="#0033A0">
                {cloneBadge}
              </Typography>
            )}
          </Box>
          <Box sx={cloneFieldSx('GAPropertySecret')}>
            <RHFTextField
              name="GAPropertySecret"
              label="Chave secreta da API do Google Analytics"
              type={isClone ? 'password' : 'text'}
              disabled={isEdit}
              value={isEdit ? '*******' : undefined}
              InputProps={{
                readOnly: isEdit,
              }}
            />
            {isClone && (
              <Typography variant="caption" color="#0033A0">
                {cloneBadge}
              </Typography>
            )}
          </Box>
        </FormSection>
        <FormSection index={3} title="Página de Login">
          <Stack spacing={2}>
            <Grid container direction="row" columnSpacing={4} rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Imagem do banner:</Typography>
                  <RHFUploadSingleFileAccelerator
                    name="customContent.loginPage.bannerImage"
                    acceptAll={false}
                    onDrop={handleDropBannerImage}
                    // Ajustar os handleDrop depois
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Imagem do conteúdo:</Typography>
                  <RHFUploadSingleFileAccelerator
                    name="customContent.loginPage.contentImage"
                    acceptAll={false}
                    onDrop={handleDropContentImage}
                  />
                </Stack>
              </Grid>
            </Grid>
            <Box />
            <RHFTextArea name="customContent.loginPage.bannerText" label="Texto do banner" />
            <Box />
            <Typography variant="subtitle2">Textos do conteúdo:</Typography>
            <Stack spacing={4}>
              <RHFTextArea
                name="customContent.loginPage.contentText.primaryText"
                label="Texto Primário"
              />
              <RHFTextArea
                name="customContent.loginPage.contentText.secondaryText"
                label="Texto Secundário"
              />
            </Stack>
            <Box />
            <Typography variant="subtitle2">Textos dos cards:</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
              <RHFTextArea
                name="customContent.loginPage.cardsText.upperLeft"
                label="Superior Esquerdo"
              />
              <RHFTextArea
                name="customContent.loginPage.cardsText.upperRight"
                label="Superior Direito"
              />
              <RHFTextArea
                name="customContent.loginPage.cardsText.bottomLeft"
                label="Inferior Esquerdo"
              />
              <RHFTextArea
                name="customContent.loginPage.cardsText.bottomRight"
                label="Inferior Direito"
              />
            </Stack>
          </Stack>
          <Box height={16} />
          <Stack direction="row" alignItems="center">
            <Typography variant="button">Ver Exemplo</Typography>
            <IconButton
              size="small"
              onClick={() => handleChangeExample('login-page')}
              aria-label="Expandir customização"
            >
              <ExpandMoreIcon
                sx={{
                  transform: openExample.includes('login-page') ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Stack>
          <Collapse in={openExample.includes('login-page')}>
            <Stack alignItems="center">
              <Box
                component="img"
                src={`/assets/illustrations/example-login-page/${image['login-page']}.png`}
                height={800}
                sx={{ objectFit: 'contain', maxWidth: '100%' }}
              />
            </Stack>
          </Collapse>
          <Box height={16} />
        </FormSection>
        <FormSection index={4} title="Página de Cadastro">
          <Stack spacing={2}>
            <Grid container direction="row" columnSpacing={4} rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography>Imagem do banner:</Typography>
                  <RHFUploadSingleFileAccelerator
                    name="customContent.registerPage.bannerImage"
                    acceptAll={false}
                    onDrop={handleDropRegisterPageBannerImage}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography>Imagem do conteúdo:</Typography>
                  <RHFUploadSingleFileAccelerator
                    name="customContent.registerPage.contentImage"
                    acceptAll={false}
                    onDrop={handleDropRegisterPageContentImage}
                  />
                </Stack>
              </Grid>
            </Grid>
            <Box />
            <RHFTextArea name="customContent.registerPage.bannerText" label="Texto do banner" />
            <Box />
            <Typography variant="subtitle2">Textos do conteúdo:</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
              <RHFTextArea
                name="customContent.registerPage.contentText.primaryText"
                label="Texto Primário"
              />
              <RHFTextArea
                name="customContent.registerPage.contentText.secondaryText"
                label="Texto Secundário"
              />
            </Stack>
            <Box />
            <Typography variant="subtitle2">Textos dos cards:</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
              <RHFTextArea
                name="customContent.registerPage.cardsText.upperLeft"
                label="Superior Esquerdo"
              />
              <RHFTextArea
                name="customContent.registerPage.cardsText.upperRight"
                label="Superior Direito"
              />
              <RHFTextArea
                name="customContent.registerPage.cardsText.bottomLeft"
                label="Inferior Esquerdo"
              />
              <RHFTextArea
                name="customContent.registerPage.cardsText.bottomRight"
                label="Inferior Direito"
              />
            </Stack>
          </Stack>
          <Box height={16} />
          <Stack direction="row" alignItems="center">
            <Typography variant="button">Ver Exemplo</Typography>
            <IconButton
              size="small"
              onClick={() => handleChangeExample('register-page')}
              aria-label="Expandir customização"
            >
              <ExpandMoreIcon
                sx={{
                  transform: openExample.includes('register-page')
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Stack>
          <Collapse in={openExample.includes('register-page')}>
            <Stack alignItems="center">
              <Box
                component="img"
                src={`/assets/illustrations/example-register-page/${image['register-page']}.png`}
                height={800}
                sx={{ objectFit: 'contain', maxWidth: '100%' }}
              />
              <MobileStepper
                variant="dots"
                steps={2}
                position="static"
                activeStep={step['register-page']}
                sx={{ maxWidth: 400, flexGrow: 1 }}
                nextButton={
                  <Button
                    size="small"
                    onClick={() => handleNext('register-page')}
                    disabled={step['register-page'] === 1}
                    sx={{ ml: 1 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="button">Avançar</Typography>
                      <Iconify icon="ri:arrow-right-double-line" />
                    </Stack>
                  </Button>
                }
                backButton={
                  <Button
                    size="small"
                    onClick={() => handleBack('register-page')}
                    disabled={step['register-page'] === 0}
                    sx={{ mr: 1 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Iconify icon="ri:arrow-left-double-line" />
                      <Typography variant="button">Voltar</Typography>
                    </Stack>
                  </Button>
                }
              />
            </Stack>
          </Collapse>
          <Box height={16} />
        </FormSection>
        <FormSection index={5} title="Página Inicial">
          <Stack spacing={2}>
            <Stack spacing={1}>
              <Typography>Imagem do banner:</Typography>
              <RHFUploadSingleFileAccelerator
                name="customContent.homePage.bannerImage"
                acceptAll={false}
                onDrop={handleDropHomePageBannerImage}
              />
            </Stack>
            <RHFTextArea
              name="customContent.homePage.bannerText.infoText"
              label="Texto Informativo"
            />
            <Typography>Texto do banner:</Typography>
            <RHFTextField
              name="customContent.homePage.bannerText.titleText"
              label="Título do banner"
            />
            <Typography>Itens do banner:</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <RHFTextField
                name="customContent.homePage.bannerText.items.item1.cardText"
                label="Texto do Card 1"
              />
              <RHFTextField
                name="customContent.homePage.bannerText.items.item1.text"
                label="Texto Auxiliar do Card"
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <RHFTextField
                name="customContent.homePage.bannerText.items.item2.cardText"
                label="Texto do Card 2"
              />
              <RHFTextField
                name="customContent.homePage.bannerText.items.item2.text"
                label="Texto Auxiliar do Card"
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <RHFTextField
                name="customContent.homePage.bannerText.items.item3.cardText"
                label="Texto do Card 3"
              />
              <RHFTextField
                name="customContent.homePage.bannerText.items.item3.text"
                label="Texto Auxiliar do Card"
              />
            </Stack>
            <Typography>Texto do conteúdo:</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
              <RHFTextArea
                name="customContent.homePage.contentText.primaryText"
                label="Texto Primário"
              />
              <RHFTextArea
                name="customContent.homePage.contentText.secondaryText"
                label="Texto Secundário"
              />
            </Stack>
          </Stack>
          <Box height={16} />
          <Stack direction="row" alignItems="center">
            <Typography variant="button">Ver Exemplo</Typography>
            <IconButton
              size="small"
              onClick={() => handleChangeExample('home-page')}
              aria-label="Expandir customização"
            >
              <ExpandMoreIcon
                sx={{
                  transform: openExample.includes('home-page') ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Stack>
          <Collapse in={openExample.includes('home-page')}>
            <Stack alignItems="center">
              <Box
                component="img"
                src={`/assets/illustrations/example-home-page/${image['home-page']}.png`}
                height={800}
                sx={{ objectFit: 'contain', maxWidth: '100%' }}
              />
            </Stack>
          </Collapse>
          <Box height={16} />
        </FormSection>
        <FormSection index={6} title="Página de Meus Benefícios">
          <RHFTextField name="customContent.myBenefitsPage.titleText" label="Título" />
          <Box height={16} />
          <Stack direction="row" alignItems="center">
            <Typography variant="button">Ver Exemplo</Typography>
            <IconButton
              size="small"
              onClick={() => handleChangeExample('benefits-page')}
              aria-label="Expandir customização"
            >
              <ExpandMoreIcon
                sx={{
                  transform: openExample.includes('benefits-page')
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Stack>
          <Collapse in={openExample.includes('benefits-page')}>
            <Stack alignItems="center">
              <Box
                component="img"
                src={`/assets/illustrations/example-benefits-page/${image['benefits-page']}.png`}
                height={400}
                sx={{ objectFit: 'contain', maxWidth: '100%' }}
              />
            </Stack>
          </Collapse>
          <Box height={16} />
        </FormSection>
        {cloneBenefits}
        {isClone && pendingCloneFields.length > 0 && (
          <Alert severity="warning">
            Para salvar esta cópia, altere:{' '}
            {pendingCloneFields.map((field) => CLONE_FIELD_LABELS[field]).join(', ')}.
          </Alert>
        )}
        <Stack justifyContent="flex-end" direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button onClick={onBack ?? (() => navigate(paths.dashboard.lpEventsList))}>Voltar</Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={
              (isEdit && !permissions?.update) ||
              (!isEdit && !permissions?.create) ||
              (isClone && pendingCloneFields.length > 0)
            }
          >
            {isClone ? 'Salvar Evento' : defaultSaveLabel}
          </LoadingButton>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
